<script setup>
import Width from "@/pages/Index/Width.vue";
import Title from "@/components/Title/Title.vue";
import {useRouter} from "vue-router";
import {useStore} from "vuex";
import {computed} from "vue";
import {extractText} from "../../../utils";
import dayjs from "dayjs";

const router = useRouter()
const store = useStore()

const props = defineProps({
	news: {
		type: Array,
		default: () => []
	}
})

const currentLang = computed(() => {
	
	const lang = store.getters.currentLanguage
	
	return lang === 'cn' ? 'zh' : lang
	
})

const firstNews = computed(() => props.news[0])

const news = computed(() => [props.news[1], props.news[2]])

const toPage = () => {
	router.push('/newsInformation')
}

const toNewsDetails = (id) => {
	router.push({
		path: `/details/${id}`
	})
}

const dateFormat = (time) => {
	const date = dayjs(time)
	const day = date.date() > 10 ? date.date() : `0${date.date()}`
	const month = date.month() + 1 >= 10 ? date.month() + 1 : `0${date.month() + 1}`
	return {
		year: date.year(),
		month,
		date: day,
		yearMonth: `${date.year()}.${month}`,
		result: `${date.year()}.${month}.${day}`
	}
}
</script>

<template>
	<div class="news">
		<Width>
			<div class="news__header">
				<Title
					mode="dark"
					position="left"
					:title="$t('section.newsBlock.title')"
				/>
				
				<div class="news__header__more" @click="toPage">
					<div class="desc">{{ $t('section.newsBlock.more') }}</div>
					<el-icon class="icon--pc" size="32"><CirclePlus /></el-icon>
					<el-icon class="icon--mobile" size="20"><CirclePlus /></el-icon>
				</div>
			</div>
			
			<div class="news__content">
				<div
					class="news__content__left"
					:style="{
						'background-image': `url(${firstNews?.cover})`
					}"
				>
					<div class="mask" />
					<div class="news__content__left__date">
						<div class="ym">
							{{ dateFormat(firstNews?.time).yearMonth }}
						</div>
						<div
							class="day"
						>
							{{ dateFormat(firstNews?.time).date }}
						</div>
					</div>
					
					<div
						class="news__content__left__title line-clamp-1"
						@click="toNewsDetails(firstNews.id)"
					>
						{{ firstNews?.[`title${currentLang}`] }}
					</div>
				</div>
				
				<div class="news__content__right">
					<div
						class="news__content__right__item"
						v-for="(item, index) in news"
						:key="index"
					>
						<div
							class="news__title"
							@click="toNewsDetails(item.id)"
						>
							<div class="line-clamp-2">{{ item?.[`title${currentLang}`] || item?.titlezh }}</div>
						</div>
						
						<div class="news__desc line-clamp-3">
							{{ extractText(item?.[`content${currentLang}`] || item?.contentzh) }}
						</div>
						
						<div class="news__date">
							<div class="ym">
								{{ dateFormat(item?.time).yearMonth }}
							</div>
							
							<div
								class="day"
								@click="toNewsDetails(item.id)"
							>
								<div class="day--pc">{{ dateFormat(item?.time).date }}</div>
								<div class="day--mobile">{{ dateFormat(item?.time).result }}</div>
								<el-icon size="24"><Right /></el-icon>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Width>
	</div>
</template>

<style scoped lang="less">
.news {
	padding: 150px 0;
	
	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 50px;
		
		&__more {
			cursor: pointer;
			display: flex;
			align-items: center;
			
			.icon--mobile {
				display: none;
			}
			
			.desc {
				color: #3D3D3D;
				font-size: 20px;
				margin-right: 20px;
			}
		}
		
	}
	
	&__content {
		display: flex;
		&__left {
			background-color: #fff;
			padding: 30px 30px 28px 36px;
			margin-right: 20px;
			width: 40%;
			background-size: cover;
			background-repeat: no-repeat;
			color: #fff;
			font-weight: bold;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			position: relative;
			
			.mask {
				position: absolute;
				left: 0;
				top: 0;
				width: 100%;
				height: 100%;
				background: radial-gradient(50% 50% at 50% 50%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.2) 100%);
			}
			
			&__date {
				text-align: right;
				position: relative;
				z-index: 2;
				.ym {
					font-size: 24px;
				}
				
				.day {
					font-size: 36px;
				}
				
			}
			
			&__title {
				font-weight: 700;
				cursor: pointer;
				font-size: 24px;
				position: relative;
				z-index: 2;
				
				&:hover {
					text-decoration: underline solid;
				}
			}
			
		}
		
		&__right {
			width: 60%;
			display: flex;
			
			&__item {
				width: 50%;
				padding: 50px 30px 30px 30px;
				background-color: #fff;
				color: #303133;
				font-size: 18px;
				
				&:first-child {
					margin-right: 20px;
				}
				
				.news__title {
					padding-bottom: 30px;
					border-bottom: 1px solid #DCDFE6;
					line-height: 26px;
					cursor: pointer;
					
					&:hover {
						font-weight: bold;
					}
					
				}
				
				.news__desc {
					margin-top: 30px;
					margin-bottom: 70px;
					color: #606266;
					line-height: 26px;
				}
				
				.news__date {
					
					.ym {
						color: #606266;
					}
					
					.day {
						cursor: pointer;
						display: flex;
						justify-content: space-between;
						align-items: center;
						font-size: 32px;
						color: #606266;
						
						&--mobile {
							display: none;
						}
					}
					
				}
			}
			
		}
		
	}
	
}

@media only screen and (max-width: 1024px) {
	.news {
		padding: 50px 0;
		
		&__header {
			margin-bottom: 20px;
		}
		
		&__content {
			flex-direction: column;
			&__left {
				width: auto;
				margin-right: 0;
				
				&__date {

					.ym {
						font-size: 24px;
					}

					.day {
						font-size: 36px;
					}

				}

				&__title {
					margin-top: 250px;

					&:hover {
						text-decoration: underline solid;
					}
				}
				
			}
			
			&__right {
				margin-top: 20px;
				width: 100%;
			}
			
		}
	}
}

@media only screen and (max-width: 768px) {
	.news {
		padding: 30px 0;
		
		&__header {
			margin-bottom: 20px;
			
			&__more {
				.icon--pc {
					display: none;
				}
				
				.icon--mobile {
					display: block;
				}
				
				.desc {
					font-size: 16px;
					margin-right: 10px;
				}
			}
			
		}
		&__content {
			flex-direction: column;
			
			&__right {
				margin-top: 20px;
				width: 100%;
			}
			
		}
	}
}

@media only screen and (max-width: 535px) {
	.news {
		
		&__header {
			margin-bottom: 20px;
		}
		&__content {
			flex-direction: column;
			
			&__left {
				width: auto;
				margin-right: 0;
				padding: 20px;
				
				&__date {
					
					.ym {
						font-size: 18px;
					}
					
					.day {
						margin-top: 4px;
						font-size: 24px;
					}
					
				}
				
				&__title {
					margin-top: 120px;
					font-size: 18px;
				}
				
			}
			
			&__right {
				margin-top: 20px;
				width: 100%;
				flex-direction: column;
				
				&__item {
					width: auto;
					padding: 20px;
					font-size: 16px;
					&:first-child {
						margin-right: 0;
					}
					
					&:last-child {
						margin-top: 20px;
					}
					
					.news__title {
						padding-bottom: 20px;
					}
					
					.news__desc {
						font-size: 14px;
						margin: 20px 0;
						line-height: 20px;
					}
					
					.news__date {
						
						.ym {
							display: none;
						}
						
						.day {
							font-size: 16px;
							
							&--pc {
								display: none;
							}
							
							&--mobile {
								display: block;
							}
						}
						
					}
				}
			}
			
		}
	}
}
</style>